import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import style from './maturity-and-duration-table.scss?inline';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './maturity-and-duration-table.constants.js';
import { DISPLAY_TAB } from '../maturity-and-duration/maturity-and-duration.constants';
import type { MaturityAndDurationTableContentObject } from './maturity-and-duration-table.model.js';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber.js';

/**
 * @tag pop-maturity-and-duration-table-component
 * @attribute { MaturityAndDurationTableContentObject } [data-component-object] MaturityAndDurationTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopMaturityAndDurationTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * MaturityAndDurationTable content object
   */
  componentObject?: MaturityAndDurationTableContentObject;

  private renderTableHead() {
    return html`
      <tr>
        <th id="name-column" scope="col">
          <beacon-table-header
            variant="normal"
            sortable="true"
            sort-direction="descending"
          >
            ${this.componentObject?.label === DISPLAY_TAB.MATURITY
              ? Constants.MATURITY_COLUMN_HEADER
              : Constants.DURATION_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="percent-portfolio-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.PERCENT_OF_PORTFOLIO_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        ${this.componentObject?.showBenchmarkData === true
          ? html` <th
              id="percent-benchmark-column"
              scope="col"
              class="right-align"
            >
              <beacon-table-header variant="normal" sortable="true">
                ${Constants.BENCHMARK_COLUMN_HEADER}
              </beacon-table-header>
            </th>`
          : ''}
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = this.componentObject?.itemList;

    // Maturity/Duration is sorted by rank number, lower rank number value means better rank
    return html`
      ${itemList?.map((item) => {
        const formattedPercentOfFund = formatToNDecimalPlaces(
          item.percentOfFund,
          2,
        );
        const formattedPercentOfBenchmark = formatToNDecimalPlaces(
          item.percentOfIndicativeBenchmark,
          2,
        );
        return html`
          <tr>
            <th
              data-beacon-sort-type="number"
              data-beacon-sort-value="${-1 * item.rank}"
            >
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${item.name || Constants.EMPTY_CELL_VALUE}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${item.percentOfFund}"
              >
                ${formattedPercentOfFund}
              </beacon-table-record>
            </td>
            ${this.componentObject?.showBenchmarkData === true
              ? html` <td>
                  <beacon-table-record
                    variant="numeral"
                    sort-value="${item.percentOfIndicativeBenchmark}"
                  >
                    ${formattedPercentOfBenchmark}
                  </beacon-table-record>
                </td>`
              : ''}
          </tr>
        `;
      })}
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="maturity-and-duration-table-content">
        <beacon-table variant="fixed" sortable="true">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>
    `);
  }
}
